
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ProcessSection() {
  const { t } = useTranslation();

  const processes = [
    {
      step: '01',
      icon: 'ri-search-eye-line',
      title: t('process.steps.step1.title'),
      description: t('process.steps.step1.description'),
      color: 'from-slate-600 to-slate-700'
    },
    {
      step: '02',
      icon: 'ri-line-chart-line',
      title: t('process.steps.step2.title'),
      description: t('process.steps.step2.description'),
      color: 'from-sky-600 to-sky-700',
      highlight: true
    },
    {
      step: '03',
      icon: 'ri-file-check-line',
      title: t('process.steps.step3.title'),
      description: t('process.steps.step3.description'),
      color: 'from-amber-500 to-orange-500',
      highlight: true
    },
    {
      step: '04',
      icon: 'ri-customer-service-2-line',
      title: t('process.steps.step4.title'),
      description: t('process.steps.step4.description'),
      color: 'from-slate-700 to-slate-800'
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* 헤더 */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 md:px-5 py-2 bg-amber-100 rounded-full mb-4 md:mb-6">
            <i className="ri-shield-check-line text-amber-600 text-base md:text-lg"></i>
            <span className="text-amber-800 font-semibold text-xs md:text-sm">{t('process.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            {t('process.title')}
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed whitespace-pre-line px-4">
            {t('process.subtitle')}
          </p>
        </div>

        {/* 프로세스 단계 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 lg:gap-12 mb-10 md:mb-16 items-start">
          {processes.map((process, index) => (
            <div key={index} className="relative flex items-start">
              <div className="relative bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 flex-1 ring-2 ring-amber-400">
                {/* 하이라이트 배지 */}
                {process.highlight && (
                  <div className="absolute -top-3 -right-3 px-2 md:px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-lg">
                    <span className="text-white text-xs font-bold">{t('process.highlight')}</span>
                  </div>
                )}

                {/* 아이콘 */}
                <div className={`w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 md:mb-6 rounded-full bg-gradient-to-br ${process.color} flex items-center justify-center shadow-lg`}>
                  <i className={`${process.icon} text-white text-2xl md:text-3xl`}></i>
                </div>

                {/* 단계 번호 */}
                <div className="text-center mb-3 md:mb-4">
                  <span className={`text-xs md:text-sm font-bold bg-gradient-to-r ${process.color} bg-clip-text text-transparent`}>
                    STEP {process.step}
                  </span>
                </div>

                {/* 제목 */}
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 text-center">
                  {process.title}
                </h3>

                {/* 설명 */}
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed text-center">
                  {process.description}
                </p>
              </div>
              
              {/* 화살표 연결 - 데스크탑만 */}
              {index < processes.length - 1 && (
                <div className="hidden lg:block absolute -right-9 top-1/2 -translate-y-1/2 z-20">
                  <div className="flex items-center">
                    <div className="w-8 h-1.5 bg-gradient-to-r from-sky-400 to-sky-500 rounded-full"></div>
                    <div className="w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-l-[14px] border-l-sky-500"></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 보증 정보 */}
        <div className="bg-gradient-to-r from-slate-50 to-sky-50 rounded-2xl md:rounded-3xl p-6 md:p-12 border-2 border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                <i className="ri-shield-star-line text-white text-2xl md:text-4xl"></i>
              </div>
              <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{t('process.warranty.freeWarranty')}</h4>
              <p className="text-sm md:text-base text-gray-600">{t('process.warranty.freeWarrantyDesc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 rounded-full bg-gradient-to-br from-sky-600 to-sky-700 flex items-center justify-center">
                <i className="ri-time-line text-white text-2xl md:text-4xl"></i>
              </div>
              <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{t('process.warranty.lifespan')}</h4>
              <p className="text-sm md:text-base text-gray-600">{t('process.warranty.lifespanDesc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-white text-2xl md:text-4xl"></i>
              </div>
              <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{t('process.warranty.roi')}</h4>
              <p className="text-sm md:text-base text-gray-600">{t('process.warranty.roiDesc')}</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-8 md:mt-12">
          <p className="text-base md:text-lg text-gray-700 mb-4 md:mb-6 font-medium px-4">
            {t('process.cta')}
          </p>
          <Link 
            to="/contact"
            className="inline-block px-8 md:px-12 py-3 md:py-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-lg text-base md:text-lg font-semibold hover:from-slate-600 hover:to-slate-700 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            {t('process.ctaButton')}
          </Link>
        </div>
      </div>

      {/* 회사소개 섹션 */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 mt-20 md:mt-32">
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full mb-6 md:mb-8">
            <span className="text-white text-lg md:text-2xl font-bold tracking-wide">ABOUT SEPIS</span>
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-serif text-gray-900 mb-4 md:mb-6 leading-tight">한국을 대표하는 절전기 제품</h2>
          <p className="text-2xl md:text-4xl lg:text-5xl font-serif text-gray-900 mb-10 md:mb-16 leading-relaxed whitespace-pre-line px-4">SEPIS는 음의 자기저항 효과를 응용하여
AI 시대의 전력 효율을 실현합니다</p>
          
          <div className="space-y-4 md:space-y-6 text-base md:text-lg text-gray-600 leading-relaxed max-w-5xl mx-auto px-4 text-center">
            <p>SEPIS는 혁신적인 음의 자기저항 효과 기술을 바탕으로 개발된 차세대 절전 솔루션입니다. AI 4차 산업시대의<br />급증하는 전력 수요에 대응하여, 최고 수준의 전력 효율성을 제공합니다.</p>
            
            <p>우리의 기술은 GLOBAL ONLY ONE으로, 세계 어디에서도 찾아볼 수 없는 독자적인 기술력을 자랑합니다.<br />SEPIS와 함께 지속가능한 에너지 미래를 만들어가세요.</p>
          </div>
        </div>

        {/* 아이콘 그리드 */}
        <div className="mt-12 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          <div className="text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 flex items-center justify-center">
              <i className="ri-lightbulb-flash-line text-4xl md:text-5xl text-[#0EA5E9]"></i>
            </div>
            <p className="text-lg md:text-2xl text-gray-600 font-bold">혁신 기술</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 flex items-center justify-center">
              <i className="ri-cpu-line text-4xl md:text-5xl text-[#0EA5E9]"></i>
            </div>
            <p className="text-lg md:text-2xl text-gray-600 font-bold">AI 최적화</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 flex items-center justify-center">
              <i className="ri-shield-check-line text-4xl md:text-5xl text-[#0EA5E9]"></i>
            </div>
            <p className="text-lg md:text-2xl text-gray-600 font-bold">품질 보증</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 flex items-center justify-center">
              <i className="ri-global-line text-4xl md:text-5xl text-[#0EA5E9]"></i>
            </div>
            <p className="text-lg md:text-2xl text-gray-600 font-bold">글로벌 인증</p>
          </div>
        </div>

        {/* Our Mission 섹션 */}
        <div className="mt-20 md:mt-32 pt-12 md:pt-20 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-sky-50 to-sky-100 p-6 md:p-10 rounded-xl shadow-md border-2 border-sky-200">
              <div className="w-14 h-14 md:w-18 md:h-18 mx-auto mb-4 md:mb-6 flex items-center justify-center bg-[#0EA5E9]/20 rounded-full">
                <i className="ri-money-dollar-circle-line text-3xl md:text-4xl text-[#0EA5E9]"></i>
              </div>
              <p className="text-lg md:text-2xl font-bold text-[#0EA5E9] leading-relaxed text-center">전기요금 절약의 확실한 해답 SEPIS!</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 md:p-10 rounded-xl shadow-md border-2 border-emerald-200">
              <div className="w-14 h-14 md:w-18 md:h-18 mx-auto mb-4 md:mb-6 flex items-center justify-center bg-emerald-500/20 rounded-full">
                <i className="ri-building-line text-3xl md:text-4xl text-emerald-600"></i>
              </div>
              <p className="text-lg md:text-2xl font-bold text-emerald-600 leading-relaxed text-center">기업 에너지 효율의 새로운 기준</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
